import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useEscape, useFocusTrap, useMediaQuery, useScrollLock, } from "../hooks.js";
import { useIsomorphicLayoutEffect } from "../internal.js";
/**
 * **LOCKED (§12): one overlay at a time.** The scrim is shared and never
 * stacks. Opening the drawer closes the flyout and vice versa — that is the
 * design import's own state machine and the app-level `useOverlay` hook is what
 * enforces it; these components only render what they are told to.
 */
/**
 * The overlay's own host node under `<body>`.
 *
 * ── T-414: the host exists from the FIRST render, not from an effect ────────
 *
 * It used to be `useState<HTMLElement | null>(null)` filled in by an effect,
 * which meant the first commit of every overlay in this file rendered **`null`**
 * — no container, no children, nothing for a parent's `ref` to point at. The
 * overlay then appeared in a second commit that the parent's one-shot focus
 * effect had already run before. That is the mechanism behind T-407 D3: the
 * shop dialog and the conversion sheet opened with focus still on the trigger.
 *
 * Creating the element in the state initialiser is a *detached* node — nothing
 * is in the document until the layout effect below appends it, in the same
 * commit as the children, so the overlay still appears atomically and `ref`s
 * are attached by the time anything can look. React may discard a second node
 * under StrictMode's double-render; a detached `<div>` that was never appended
 * is garbage, not a leak.
 *
 * The append is a layout effect for the same reason everything else here is:
 * the host must be in the document before the consumer's own layout effects
 * (the focus trap) run, and children's effects run before their parent's.
 */
function OverlayPortal({ children }) {
    const [host] = useState(() => {
        if (typeof document === "undefined")
            return null;
        const node = document.createElement("div");
        node.setAttribute("data-wr-overlay-root", "");
        return node;
    });
    useIsomorphicLayoutEffect(() => {
        if (!host)
            return;
        document.body.appendChild(host);
        return () => {
            host.remove();
        };
    }, [host]);
    if (!host)
        return null;
    return createPortal(children, host);
}
/**
 * `tabIndex={-1}` (T-414): the scrim is a *pointer* affordance. It stays a real
 * `<button>` so a tap has something to hit and so it is not an interactive
 * `<div>`, but it is not a stop in the tab order — the keyboard exit from an
 * overlay is `Esc`, and a scrim that answers `Tab` is a control **outside** the
 * `aria-modal` container that the keyboard could reach. QA measured exactly that
 * on the deployed build (T-407 D3: "`Tab` #1 lands on the scrim button").
 */
export function Scrim({ onDismiss, label }) {
    return (_jsx("button", { type: "button", className: "wr-scrim", "aria-label": label, onClick: onDismiss, tabIndex: -1 }));
}
/**
 * The same veil with nothing behind it (§14.1 r3): the overlay it belongs to
 * cannot be dismissed, so a scrim that *looks* tappable is a promise the app
 * will refuse to keep. A `div`, not a disabled `button` — there is no control
 * here to disable.
 */
export function StaticScrim() {
    return _jsx("div", { className: "wr-scrim wr-scrim--static", "aria-hidden": "true" });
}
/** Left slide-in navigation drawer (§11). */
export function NavDrawer({ open, onClose, label, children, testId }) {
    const trapRef = useFocusTrap(open);
    useScrollLock(open);
    useEscape(open, onClose);
    if (!open)
        return null;
    return (_jsxs(OverlayPortal, { children: [_jsx(Scrim, { onDismiss: onClose, label: label }), _jsx("div", { ref: trapRef, tabIndex: -1, className: "wr-drawer", role: "dialog", "aria-modal": "true", "aria-label": label, "data-testid": testId ?? "nav-drawer", children: children })] }));
}
export function NavItem({ label, monogram, accent, active = false, soonLabel, onSelect, testId, }) {
    const stub = soonLabel !== undefined;
    return (_jsx("li", { children: _jsxs("button", { type: "button", className: ["wr-nav-item", active ? "wr-nav-item--active" : ""].filter(Boolean).join(" "), "aria-disabled": stub || undefined, "aria-current": active ? "page" : undefined, "data-testid": testId, 
            // A stub row does nothing at all — no toast; the chip already said it.
            onClick: stub ? undefined : onSelect, children: [_jsx("span", { className: "wr-nav-item__tile", style: { background: `var(--wr-${accent})` }, "data-art-slot": `wr-icon-nav-${label.toLowerCase()}`, "aria-hidden": "true", children: monogram }), _jsx("span", { className: "wr-label-md", children: label }), stub ? _jsx("span", { className: "wr-nav-item__soon wr-mono-sm", children: soonLabel }) : null] }) }));
}
export function ProfileFlyout({ open, onClose, label, children, forceSheet = false, testId, }) {
    const narrow = useMediaQuery("(max-width: 359px)");
    const shortViewport = useMediaQuery("(max-height: 520px)");
    const trapRef = useFocusTrap(open);
    useScrollLock(open);
    useEscape(open, onClose);
    if (!open)
        return null;
    const asSheet = forceSheet || narrow || shortViewport;
    return (_jsxs(OverlayPortal, { children: [_jsx(Scrim, { onDismiss: onClose, label: label }), _jsxs("div", { ref: trapRef, tabIndex: -1, className: asSheet ? "wr-sheet" : "wr-flyout", role: "dialog", "aria-modal": "true", "aria-label": label, "data-testid": testId ?? "profile-flyout", "data-container": asSheet ? "sheet" : "flyout", children: [asSheet ? _jsx("div", { className: "wr-sheet__handle", "aria-hidden": "true" }) : null, children] })] }));
}
/**
 * The conversion container (§14): bottom sheet on mobile, dialog on desktop.
 * **Not a route** — the player must land back exactly where they were.
 */
export function BottomSheet({ open, onClose, label, children, testId, dismissible = true, expanded = false, restoreFocusTo, }) {
    const desktop = useMediaQuery("(min-width: 768px)");
    const trapRef = useFocusTrap(open, { restoreFallback: restoreFocusTo });
    useScrollLock(open);
    useEscape(open && dismissible, onClose);
    if (!open)
        return null;
    return (_jsxs(OverlayPortal, { children: [dismissible ? _jsx(Scrim, { onDismiss: onClose, label: label }) : _jsx(StaticScrim, {}), desktop ? (
            /* The `Dialog` keeps its 420 max-width in every state (§14.1 r4). */
            _jsx("div", { className: "wr-dialog-wrap", children: _jsx("div", { ref: trapRef, tabIndex: -1, className: "wr-dialog", role: "dialog", "aria-modal": "true", "aria-label": label, "data-testid": testId ?? "bottom-sheet", "data-container": "dialog", "data-dismissible": dismissible ? undefined : "false", children: children }) })) : (_jsxs("div", { ref: trapRef, tabIndex: -1, className: expanded ? "wr-sheet wr-sheet--full" : "wr-sheet", role: "dialog", "aria-modal": "true", "aria-label": label, "data-testid": testId ?? "bottom-sheet", "data-container": "sheet", "data-dismissible": dismissible ? undefined : "false", children: [dismissible ? _jsx("div", { className: "wr-sheet__handle", "aria-hidden": "true" }) : null, children] }))] }));
}
/** Centred dialog with a scrim and a focus trap (§11 guest log-out). */
export function Dialog({ open, onClose, label, children, testId }) {
    const trapRef = useFocusTrap(open);
    useScrollLock(open);
    useEscape(open, onClose);
    if (!open)
        return null;
    return (_jsxs(OverlayPortal, { children: [_jsx(Scrim, { onDismiss: onClose, label: label }), _jsx("div", { className: "wr-dialog-wrap", children: _jsx("div", { ref: trapRef, tabIndex: -1, className: "wr-dialog", role: "dialog", "aria-modal": "true", "aria-label": label, "data-testid": testId ?? "dialog", children: children }) })] }));
}
//# sourceMappingURL=Overlays.js.map